/**
 * <cart-free-shipping> — Free Shipping Progress Bar Web Component
 *
 * WHY A CUSTOM ELEMENT?
 * The cart drawer re-renders by replacing .drawer__inner's innerHTML on every
 * cart update (see CartDrawerItems.getSectionsToRender in cart-drawer.js).
 * This destroys and recreates every child element, including this bar.
 * A plain CSS transition on `width` would therefore always start from 0%
 * instead of from the previous percentage, because there is no "previous state"
 * on a freshly created element.
 *
 * HOW THE ANIMATION WORKS:
 * Each time the element is inserted into the DOM, connectedCallback fires.
 * The new element's fill div carries the target percentage in data-width
 * (written by Liquid at render time). The previous percentage is stored in
 * window._cartFreeShippingPct and survives the innerHTML replacement.
 *
 * Steps:
 *  1. Disable the CSS transition and snap the fill to the PREVIOUS percentage.
 *  2. Wait two animation frames so the browser commits that position.
 *  3. Re-enable the CSS transition and set the fill to the NEW percentage.
 *  4. The browser now animates from prev → new, not from 0 → new.
 *  5. Save the new percentage for the next re-render.
 */
class CartFreeShipping extends HTMLElement {
  connectedCallback() {
    const fill = this.querySelector('.cart-free-shipping__bar-fill');
    if (!fill) return;

    // Target percentage is set by Liquid on the data attribute.
    const targetPct = parseInt(fill.dataset.width || 0);
    // Previous percentage persists across re-renders via window.
    const prevPct = window._cartFreeShippingPct ?? 0;

    // Jump to previous position without animating, then animate to new position.
    fill.style.transition = 'none';
    fill.style.width = prevPct + '%';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fill.style.transition = '';
        fill.style.width = targetPct + '%';
      });
    });

    window._cartFreeShippingPct = targetPct;
  }
}

customElements.define('cart-free-shipping', CartFreeShipping);

/**
 * <cart-free-gift> — Free Gift Card Web Component
 *
 * State is determined entirely by Liquid at render time based on cart_free_gift_threshold.
 * No JS animation is needed — the element simply switches CSS classes (--unlocked) on
 * each re-render. Defined as a custom element for structural consistency with
 * <cart-free-shipping> and to allow future JS enhancements if needed.
 */
class CartFreeGift extends HTMLElement {}

customElements.define('cart-free-gift', CartFreeGift);
